import { useContextTS } from "@inrange/utils";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DeploymentStatsInitResponse,
  DeploymentStatsScope,
  InProgressDeploymentStatsResponse,
  OperationalDeploymentStatsResponse,
  SiteMetrics,
} from "../http/getOperationalDeploymentStats";
import { AsyncOperationProgress } from "../http/getAsyncStatus";
import { ReloginContext } from "../ReloginContext";
import { useAsyncOperation } from "./useAsyncOperation";
import { getAuthTokenBlocking } from "./utils";
import postOperationalDeploymentStats from "../http/getOperationalDeploymentStats";

const SITE_ID = "global";
const OPERATIONAL_CACHE_KEY = ["sites", "deployment_stats", "operational"];
const IN_PROGRESS_CACHE_KEY = ["sites", "deployment_stats", "in_progress"];
const CACHE_TTL_MS = 5 * 60 * 1000;

interface ScopeState<TData> {
  isLoading: boolean;
  isError: boolean;
  error?: string;
  data: TData | null;
  progress?: AsyncOperationProgress;
}

const useScopedDeploymentStats = <TData>(
  scope: DeploymentStatsScope,
  cacheKey: readonly unknown[]
): ScopeState<TData> => {
  const { getToken } = useKindeAuth();
  const { relogin } = useContextTS(ReloginContext);
  const queryClient = useQueryClient();
  const [operationKey, setOperationKey] = useState<string | null>(null);

  const cachedQuery = useQuery<TData>(
    cacheKey,
    () => Promise.reject("no data"),
    {
      enabled: false,
      staleTime: CACHE_TTL_MS,
      cacheTime: CACHE_TTL_MS,
    }
  );

  const hasFreshCache =
    cachedQuery.data !== undefined && !cachedQuery.isStale;

  const initMutation = useMutation<DeploymentStatsInitResponse>(async () =>
    postOperationalDeploymentStats(
      await getAuthTokenBlocking(getToken, relogin),
      scope
    )
  );

  const asyncOperation = useAsyncOperation({
    siteId: SITE_ID,
    operationKey,
  });

  useEffect(() => {
    if (hasFreshCache) return;
    if (!initMutation.isIdle) return;
    initMutation.mutate(undefined, {
      onSuccess: (response) => {
        setOperationKey(response.operationKey);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      asyncOperation.data?.status === "completed" &&
      asyncOperation.data.result
    ) {
      queryClient.setQueryData(
        cacheKey,
        asyncOperation.data.result as unknown as TData
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asyncOperation.data?.status, asyncOperation.data?.result]);

  const isLoading =
    !hasFreshCache &&
    (initMutation.isLoading ||
      asyncOperation.isPolling ||
      (asyncOperation.data?.status !== "completed" &&
        asyncOperation.data?.status !== "failed" &&
        !asyncOperation.timedOut &&
        !initMutation.isError));

  const isError =
    !hasFreshCache &&
    (initMutation.isError ||
      asyncOperation.data?.status === "failed" ||
      asyncOperation.timedOut);

  const error = hasFreshCache
    ? undefined
    : asyncOperation.data?.error ??
      (asyncOperation.timedOut ? "Operation timed out" : undefined) ??
      (initMutation.isError ? "Failed to initiate" : undefined);

  return {
    isLoading,
    isError,
    error,
    data: cachedQuery.data ?? null,
    progress: asyncOperation.data?.progress,
  };
};

interface InProgressState {
  isLoading: boolean;
  isError: boolean;
  error?: string;
  sites: SiteMetrics[] | null;
  progress?: AsyncOperationProgress;
}

const useOperationalDeploymentStats = () => {
  const operational = useScopedDeploymentStats<OperationalDeploymentStatsResponse>(
    "operational",
    OPERATIONAL_CACHE_KEY
  );
  const inProgress = useScopedDeploymentStats<InProgressDeploymentStatsResponse>(
    "inProgress",
    IN_PROGRESS_CACHE_KEY
  );

  const inProgressResult: InProgressState = {
    isLoading: inProgress.isLoading,
    isError: inProgress.isError,
    error: inProgress.error,
    sites: inProgress.data?.sites ?? null,
    progress: inProgress.progress,
  };

  return {
    fetchDeploymentStats: operational,
    fetchInProgressDeploymentStats: inProgressResult,
  };
};

export default useOperationalDeploymentStats;
